import React from 'react';
import PropTypes from 'prop-types';
import { LuBadgeCheck } from 'react-icons/lu';

import PaginationBar from '../../../../components/PaginationBar/PaginationBar';
import {
   Card,
   CardHead,
   CardHint,
   CardTitle,
   DarkTable,
   EmptyText,
   EmptyTitle,
   EmptyWrap,
   Pill,
   RowIconButton,
   SkeletonBlock,
   TableScroll,
} from '../../../../styles/dunning/base';
import {
   formatBRL,
   formatDateBR,
   formatDoc,
   formatTimestamp,
} from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Relatório do acerto — A LINHA É O TÍTULO, não o caso (0103 §5.5). É o que o
// gestor confere contra o relatório do escritório.
//
// SEM ORDENAÇÃO por cabeçalho, de propósito: o backend não expõe `order_by`
// neste recurso, e cabeçalho clicável que não ordena é ordenação fantasma
// (navegacao.md §1.10, item 4 — "listagem sem ordenação é preferível a
// ordenação que não funciona"). Se o backend passar a aceitar `order_by`, aí
// sim entra o padrão do `thead` com SORTABLE_KEYS espelhando a whitelist.
//
// Paginação é SERVER-SIDE (`meta`): a troca de página refaz a busca.
// ---------------------------------------------------------------------------

export default function ReconciliationTable({
   rows,
   meta = null,
   loading,
   canReconcile,
   onPageChange,
   onAskCheck,
}) {
   const total = meta?.total ?? 0;
   const totalPages = meta?.totalPages ?? 1;
   const currentPage = meta?.page ?? 1;

   return (
      <Card>
         <CardHead>
            <CardTitle>Títulos do acerto</CardTitle>
            {!loading && (
               <CardHint>
                  {`${total} ${total === 1 ? 'título' : 'títulos'} no recorte`}
               </CardHint>
            )}
         </CardHead>

         {loading ? (
            <div style={{ padding: 16, display: 'grid', gap: 10 }}>
               {[0, 1, 2, 3, 4, 5].map((i) => (
                  <SkeletonBlock key={i} $h="34px" $radius="8px" />
               ))}
            </div>
         ) : (
            <>
               <TableScroll>
                  <DarkTable>
                     <thead>
                        <tr>
                           <th>Cliente</th>
                           <th>Parcela</th>
                           <th>Vencimento</th>
                           <th>Pagamento</th>
                           <th className="num">Valor</th>
                           <th>Enviado ao escritório</th>
                           <th>Conferência</th>
                           {canReconcile && (
                              <th className="num" aria-label="Ações" />
                           )}
                        </tr>
                     </thead>
                     <tbody>
                        {rows.map((row) => (
                           <tr key={`${row.dunning_id}-${row.charge_id}`}>
                              <td>
                                 <div
                                    style={{ fontWeight: 700, fontSize: 12 }}
                                 >
                                    {row.customer_name}
                                 </div>
                                 <div
                                    style={{
                                       fontSize: 11,
                                       color: '#8a8a8a',
                                       fontVariantNumeric: 'tabular-nums',
                                    }}
                                 >
                                    {formatDoc(row.customer_doc)}
                                 </div>
                              </td>
                              <td>
                                 <div style={{ fontWeight: 700 }}>
                                    {row.installment_number
                                       ? `${row.installment_number}/${row.installment_count}`
                                       : 'Avulso'}
                                 </div>
                                 <div
                                    style={{ fontSize: 11, color: '#8a8a8a' }}
                                 >
                                    {row.order_number
                                       ? `Pedido ${row.order_number}`
                                       : '—'}
                                    {row.nfe_number
                                       ? ` · NF ${row.nfe_number}`
                                       : ''}
                                 </div>
                              </td>
                              <td
                                 style={{
                                    fontVariantNumeric: 'tabular-nums',
                                 }}
                              >
                                 {formatDateBR(row.due_date)}
                              </td>
                              <td
                                 style={{
                                    fontVariantNumeric: 'tabular-nums',
                                 }}
                              >
                                 {formatDateBR(row.payment_date)}
                              </td>
                              <td
                                 className="num"
                                 style={{
                                    fontWeight: 700,
                                    fontVariantNumeric: 'tabular-nums',
                                 }}
                              >
                                 {formatBRL(row.value)}
                              </td>
                              <td style={{ color: '#666', fontSize: 11 }}>
                                 {formatTimestamp(row.sent_to_external_at)}
                              </td>
                              <td>
                                 {row.checked ? (
                                    <>
                                       <Pill $variant="success">
                                          <LuBadgeCheck size={11} />
                                          conferido
                                       </Pill>
                                       <div
                                          style={{
                                             fontSize: 11,
                                             color: '#8a8a8a',
                                             marginTop: 3,
                                          }}
                                       >
                                          {[
                                             formatTimestamp(row.checked_at),
                                             row.checked_by?.nome,
                                          ]
                                             .filter(Boolean)
                                             .join(' · ')}
                                       </div>
                                       {row.checked_note && (
                                          <div
                                             style={{
                                                fontSize: 11,
                                                color: '#666',
                                                marginTop: 2,
                                             }}
                                          >
                                             {row.checked_note}
                                          </div>
                                       )}
                                    </>
                                 ) : (
                                    <Pill $variant="muted">não conferido</Pill>
                                 )}
                              </td>
                              {canReconcile && (
                                 <td className="num">
                                    <RowIconButton
                                       type="button"
                                       title={
                                          row.checked
                                             ? 'Desfazer conferência'
                                             : 'Marcar como conferido'
                                       }
                                       onClick={() => onAskCheck(row)}
                                    >
                                       <LuBadgeCheck size={15} />
                                    </RowIconButton>
                                 </td>
                              )}
                           </tr>
                        ))}
                     </tbody>
                  </DarkTable>
               </TableScroll>

               {rows.length === 0 && (
                  <EmptyWrap>
                     <EmptyTitle>Nenhum título no recorte</EmptyTitle>
                     <EmptyText>
                        Ajuste os filtros e pesquise novamente.
                     </EmptyText>
                  </EmptyWrap>
               )}

               {totalPages > 1 && (
                  <div
                     style={{
                        padding: '4px 16px 12px',
                        borderTop: '1px solid rgba(0, 0, 0, 0.06)',
                     }}
                  >
                     <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={onPageChange}
                     />
                  </div>
               )}
            </>
         )}
      </Card>
   );
}

ReconciliationTable.propTypes = {
   // eslint-disable-next-line react/forbid-prop-types
   rows: PropTypes.array.isRequired,
   // eslint-disable-next-line react/forbid-prop-types
   meta: PropTypes.object,
   loading: PropTypes.bool.isRequired,
   canReconcile: PropTypes.bool.isRequired,
   onPageChange: PropTypes.func.isRequired,
   onAskCheck: PropTypes.func.isRequired,
};
